/**
 * CANOA — Slide Sound (viewport-triggered video playback + sound, per-slide opt-in)
 *
 * Slide videos autoplay muted (browser policy — unmuted autoplay is blocked
 * everywhere without user activation). For a slide that opts into sound, this
 * element gates PLAYBACK itself to the viewport and makes the video audible
 * as far as each browser allows:
 *
 *  • Page load → the video is paused on its first frame (the stock autoplay
 *    would otherwise run muted in the background — D57).
 *  • Viewport entry (≥60% of the slide visible) → start from 0 and attempt
 *    unmuted play(). Chrome/Edge allow it once the visitor has clicked/tapped
 *    anything this page load (sticky activation). If the browser refuses, the
 *    video plays muted and the speaker pill shows the muted state.
 *  • Blocked → a one-time document-level gesture retry: the visitor's next
 *    click/tap anywhere re-attempts sound synchronously inside the gesture
 *    (transient activation — this is also the only path Safari/iOS accept).
 *  • Viewport exit (<25% visible) → volume fades out, then the video mutes
 *    AND pauses; the next entry restarts from 0. The 60/25 hysteresis uses
 *    intersectionRatio, not isIntersecting (which stays true while any pixel
 *    is visible — the D57 fade-out bug).
 *
 * STICKY-STACK COVERAGE (D58): the homepage slideshows are `position: sticky`
 * (the sticky-stack reveal pins each section while the next scrolls OVER it),
 * so on vertical scroll the slide never geometrically leaves the viewport —
 * IntersectionObserver measures geometry, not occlusion, and the exit never
 * fired (Laura's report; horizontal paging worked because the slide really
 * does leave the scroller). Fix: a zero-size SENTINEL inserted after the
 * sticky section in normal flow. The sentinel doesn't stick, so its position
 * IS the covering edge of the next section: when it rises past the exit line
 * (25% of the slide height below the pin offset), the slide is ≥75% covered →
 * fade out + park; back below the enter line (60%) → uncovered again →
 * re-enter. Effective in-view = geometric IO AND not covered.
 *
 *  • The pill is always tappable: manual mute is sticky (the video keeps
 *    looping muted while in view); manual unmute always works (it IS a
 *    gesture). Only one slide is audible at a time.
 *
 * Clicks/presses are swallowed so the slide's /select navigation and the
 * carousel drag-nudge never fire (same pattern as slide-reveal.js).
 * In the theme editor everything is skipped (stock muted autoplay; the pill
 * still works). iOS ignores element volume, so fades become hard cuts there.
 */

const ENTER_RATIO = 0.6; // “substantially in view” before playback + sound start
const EXIT_RATIO = 0.25; // fade out + pause once mostly gone/covered (hysteresis)
const FADE_IN_MS = 600;
const FADE_OUT_MS = 400;

class SlideSound extends HTMLElement {
  static instances = new Set();

  connectedCallback() {
    this.slide = this.closest('slideshow-slide');
    this.btn = this.querySelector('[data-ss-toggle]');
    this.audible = false;
    this.userMuted = false;
    this._inView = null; // applied state — null until first evaluation
    this._geomIn = null; // slide geometrically in viewport (slide IO)
    this._covered = false; // sticky-pinned slide occluded by the next section
    const inEditor = window.Shopify && window.Shopify.designMode;
    this._gated = !inEditor && 'IntersectionObserver' in window && !!this.slide;

    this._onToggle = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.audible) {
        this.disable(true); // manual mute — sticky
      } else {
        this.userMuted = false;
        this.enable(); // inside the click gesture → allowed everywhere
      }
    };
    this._onDown = (event) => event.stopPropagation();
    this._downEvents = ['pointerdown', 'mousedown', 'touchstart'];
    this.btn?.addEventListener('click', this._onToggle);
    for (const type of this._downEvents) this.btn?.addEventListener(type, this._onDown);

    if (this._gated) {
      // Hold playback until the slide is genuinely in view. The video carries
      // the stock autoplay attribute (it may have run for a moment before this
      // module evaluated — it loads lazily), so pause + rewind right away; the
      // observers' initial callbacks decide the real state.
      this.#parkAll();
      this._io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
            if (ratio >= ENTER_RATIO) this.#setGeom(true);
            else if (ratio < EXIT_RATIO) this.#setGeom(false);
            // between the two thresholds: hysteresis — keep the current state
          }
        },
        { threshold: [EXIT_RATIO, ENTER_RATIO] }
      );
      this._io.observe(this.slide);
      this.#setupCoverWatch();
    }

    // D68 — first-frame warm. Gated videos (no autoplay attr) paint nothing
    // until played, leaving the poster <img> exposed — and Shopify's
    // auto-generated video poster can resolve to a low-res variant that reads
    // as a hazy "overlay" over the slide (Laura's report: visible only on
    // this video, only while loading). Once the video has data (slide-prefetch
    // upgrades it to preload=auto ~1.5 screens ahead), a sub-frame seek forces
    // the browser to decode + paint the REAL frame 0, which covers the poster
    // before the slide is ever seen. enable() re-seeks to 0 anyway.
    this._onWarm = (event) => {
      const v = event.target;
      if (v.paused && !this._inView) {
        try {
          v.currentTime = 0.001;
        } catch (e) {
          /* not seekable */
        }
      }
    };
    for (const v of this.#videos()) v.addEventListener('loadeddata', this._onWarm, { once: true });

    // bfcache restore resumes autoplay media and can desync our state — reset
    // to parked/muted and re-derive from the last known viewport position.
    this._onPageshow = (event) => {
      if (!event.persisted || !this._gated) return;
      this.audible = false;
      this.removeAttribute('audible');
      this.#syncButton();
      this._inView = null;
      this.#parkAll();
      this.#applyState();
    };
    window.addEventListener('pageshow', this._onPageshow);

    SlideSound.instances.add(this);
  }

  disconnectedCallback() {
    this.btn?.removeEventListener('click', this._onToggle);
    for (const type of this._downEvents || []) this.btn?.removeEventListener(type, this._onDown);
    this._io?.disconnect();
    this._coverIoEnter?.disconnect();
    this._coverIoExit?.disconnect();
    this._sentinel?.remove();
    if (this._onCoverResize) window.removeEventListener('resize', this._onCoverResize);
    clearTimeout(this._coverResizeTimer);
    window.removeEventListener('pageshow', this._onPageshow);
    for (const v of this.#videos()) v.removeEventListener('loadeddata', this._onWarm);
    this.#disarmKeepalive();
    this.#disarmRetry();
    cancelAnimationFrame(this._fadeRaf);
    clearTimeout(this._settleTimer);
    SlideSound.instances.delete(this);
  }

  // The slide can hold a desktop + a mobile <video> (one display:none’d by the
  // breakpoint utilities) — play the visible one, park both.
  video() {
    if (!this.slide) return null;
    const vids = this.slide.querySelectorAll('video.slide__video');
    for (const v of vids) {
      if (getComputedStyle(v).display !== 'none') return v;
    }
    return vids[0] || null;
  }

  #videos() {
    return this.slide ? this.slide.querySelectorAll('video.slide__video') : [];
  }

  // Paused + muted. Deliberately NOT rewound (D65): seeking to 0 while the
  // slide is still partially visible (the exit threshold fires at <25%, not
  // 0%) snapped the frame in view — the "tiny glitch" on boundary wiggles.
  // enable() seeks to 0 itself, so restart-from-0 on the next entry is intact.
  #parkAll() {
    for (const v of this.#videos()) {
      v.pause();
      v.muted = true;
      v.volume = 1;
    }
  }

  // ── Combined in-view state: geometry AND occlusion ────────────────────────
  #setGeom(inView) {
    if (this._geomIn === inView) return;
    this._geomIn = inView;
    this.#applyState();
  }

  #setCovered(covered) {
    if (this._covered === covered) return;
    this._covered = covered;
    this.#applyState();
  }

  #applyState() {
    if (this._geomIn === null) return; // wait for the slide IO's first report
    const want = this._geomIn === true && !this._covered;
    clearTimeout(this._settleTimer);
    if (want === (this._inView === true)) return; // already in the target state
    if (this._inView === null) {
      // First evaluation (page load / bfcache resync): apply immediately.
      if (want) this.#enterView();
      else this.#leaveView();
      return;
    }
    // Settle delay (D65): a scroll wiggle at a threshold boundary flipped the
    // state per crossing, and each flip seeks/plays/pauses the video — a
    // visible frame glitch + decoder churn right where Laura felt it. Apply a
    // state change only once it has held for a beat; re-evaluate the flags at
    // fire time (they may have flipped back — then nothing happens).
    this._settleTimer = setTimeout(() => {
      const settled = this._geomIn === true && !this._covered;
      if (settled === (this._inView === true)) return;
      if (settled) this.#enterView();
      else this.#leaveView();
    }, 250);
  }

  // ── Sticky-stack occlusion watch (D58) ────────────────────────────────────
  // Only armed when the enclosing Shopify section is actually sticky (the
  // sticky-stack reveal; reduced-motion users get normal flow and skip this).
  // The sentinel lives AFTER the section in normal flow, so while the section
  // is pinned the sentinel's top edge = where the next section starts covering
  // it. Two one-line observers (rootMargin-shrunk roots, threshold 0) recreate
  // the same 60/25 hysteresis as the slide IO, with lines measured from the
  // pin offset. Rebuilt on resize (the lines are computed in px from dvh-based
  // heights).
  #setupCoverWatch() {
    const section = this.slide.closest('.shopify-section') || this.slide.closest('[id^="shopify-section-"]');
    if (!section) return;
    if (getComputedStyle(section).position !== 'sticky') return;

    this._sentinel = document.createElement('div');
    this._sentinel.setAttribute('aria-hidden', 'true');
    this._sentinel.style.cssText = 'position:relative;width:1px;height:1px;margin:0;padding:0;border:0;pointer-events:none;visibility:hidden;';
    section.insertAdjacentElement('afterend', this._sentinel);

    const build = () => {
      this._coverIoEnter?.disconnect();
      this._coverIoExit?.disconnect();
      const slideH = this.slide.getBoundingClientRect().height || window.innerHeight;
      const pinTop = parseFloat(getComputedStyle(section).top) || 0;
      const exitLine = Math.round(pinTop + slideH * EXIT_RATIO);
      const enterLine = Math.round(pinTop + slideH * ENTER_RATIO);

      // Exit: sentinel above the exit line → the next section covers ≥75% of
      // the pinned slide. (Not-intersecting also happens while the sentinel is
      // still BELOW the viewport — section not reached yet — so check the side.)
      this._coverIoExit = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting && entry.boundingClientRect.top < exitLine) {
              this.#setCovered(true);
            }
          }
        },
        { rootMargin: `-${exitLine}px 0px 0px 0px`, threshold: 0 }
      );
      this._coverIoExit.observe(this._sentinel);

      // Enter: sentinel back at/below the enter line → ≥60% uncovered again.
      this._coverIoEnter = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting || entry.boundingClientRect.top >= enterLine) {
              this.#setCovered(false);
            }
          }
        },
        { rootMargin: `-${enterLine}px 0px 0px 0px`, threshold: 0 }
      );
      this._coverIoEnter.observe(this._sentinel);
    };
    build();

    // The trigger lines are px snapshots of dvh-based heights — rebuild after
    // resizes (debounced; observers re-report on observe(), so state self-heals).
    this._onCoverResize = () => {
      clearTimeout(this._coverResizeTimer);
      this._coverResizeTimer = setTimeout(build, 250);
    };
    window.addEventListener('resize', this._onCoverResize);
  }

  #enterView() {
    if (this._inView === true) return;
    this._inView = true;
    if (this.userMuted) {
      // Sticky manual mute: the slide still comes alive on entry — just silent.
      const v = this.video();
      if (v) {
        try {
          v.currentTime = 0;
        } catch (e) {}
        this.#playMutedKeepalive(v);
      }
      return;
    }
    this.enable();
  }

  #leaveView() {
    if (this._inView === false) return;
    const wasNull = this._inView === null;
    this._inView = false;
    if (this.audible) {
      this.audible = false;
      this.removeAttribute('audible');
      this.#syncButton();
      const v = this.video();
      if (v) {
        this.#fade(v, 0, FADE_OUT_MS, () => this.#parkAll());
      }
    } else if (!wasNull) {
      this.#parkAll();
    }
    // wasNull → initial evaluation on a below-the-fold slide: already parked
    // at connect; nothing to do (and nothing to fade).
  }

  // D75 — persistent muted keepalive. The visual must never die on a rejected
  // play(): desktop Safari (private windows, Low Power Mode) can reject even a
  // MUTED programmatic play, and a trackpad scroll never fires pointerdown, so
  // the gesture retry (#armRetry) may never run — leaving the slide parked
  // forever on its blurry upscaled auto-poster (the giant-blur screenshot).
  // Retry the muted start whenever the media gets more ready or the tab
  // returns to the foreground, only while the slide is still in view.
  #playMutedKeepalive(v) {
    v.muted = true;
    const tryPlay = () => {
      if (!this._inView || this.audible || !v.paused) return;
      v.play().catch(() => {});
    };
    tryPlay();
    // Disarm any prior arm before re-arming (D76): a one-shot `_keepaliveArmed`
    // latch silently killed the keepalive on every LATER viewport entry whose
    // play() failed (succeed once -> leave -> re-enter -> fail = parked on the
    // blurry poster again), and a stale tryPlay closure stayed attached to the
    // old video. Re-arm fresh against the current element every time.
    this.#disarmKeepalive();
    this._keepaliveArmed = true;
    this._onMediaReady = tryPlay;
    v.addEventListener('canplay', this._onMediaReady);
    v.addEventListener('loadeddata', this._onMediaReady);
    this._onVisibleRetry = () => {
      if (!document.hidden) tryPlay();
    };
    document.addEventListener('visibilitychange', this._onVisibleRetry);
  }

  #disarmKeepalive() {
    if (!this._keepaliveArmed) return;
    this._keepaliveArmed = false;
    for (const v of this.#videos()) {
      v.removeEventListener('canplay', this._onMediaReady);
      v.removeEventListener('loadeddata', this._onMediaReady);
    }
    document.removeEventListener('visibilitychange', this._onVisibleRetry);
  }

  enable() {
    if (this.audible || this.userMuted) return;
    const v = this.video();
    if (!v) return;

    // One audible video at a time.
    for (const inst of SlideSound.instances) {
      if (inst !== this) inst.disable(false);
    }

    cancelAnimationFrame(this._fadeRaf);
    // Restart so the audio/visual story begins at the beginning, then attempt
    // unmuted playback. Everything before play() is synchronous so a call from
    // inside a gesture handler keeps its transient activation.
    try {
      v.currentTime = 0;
    } catch (e) {
      /* not seekable yet — sound can still start mid-stream */
    }
    v.muted = false;
    v.volume = 0;
    const attempt = v.play();
    const onResult = (ok) => {
      // Some browsers don't reject — they pause or silently re-mute instead.
      if (!ok || v.paused || v.muted) {
        v.muted = true;
        v.volume = 1;
        // Keep the visual alive while in view; sound waits for a gesture.
        if (!this._gated || this._inView) this.#playMutedKeepalive(v);
        this.#armRetry();
        return;
      }
      this.audible = true;
      this.setAttribute('audible', '');
      this.#syncButton();
      this.#disarmRetry();
      this.#fade(v, 1, FADE_IN_MS);
    };
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(() => onResult(true)).catch(() => onResult(false));
    } else {
      onResult(true);
    }
  }

  // Audio-only off (used for manual mute and the one-audible-at-a-time rule):
  // the video keeps playing muted while in view; pausing belongs to #leaveView.
  disable(manual) {
    if (manual) this.userMuted = true;
    if (!this.audible) return;
    this.audible = false;
    this.removeAttribute('audible');
    this.#syncButton();
    const v = this.video();
    if (!v) return;
    this.#fade(v, 0, manual ? 250 : FADE_OUT_MS, () => {
      v.muted = true;
      v.volume = 1; // leave volume sane for the next unmute
    });
  }

  // One-time page-level gesture retry: the next click/tap anywhere re-attempts
  // sound *synchronously inside the gesture* (capture phase, so component-level
  // stopPropagation can't starve it). Removed on success/teardown.
  #armRetry() {
    if (this._retryArmed) return;
    this._retryArmed = true;
    this._onGesture = () => {
      if (this._inView && !this.userMuted && !this.audible) this.enable();
    };
    document.addEventListener('pointerdown', this._onGesture, true);
    document.addEventListener('keydown', this._onGesture, true);
  }

  #disarmRetry() {
    if (!this._retryArmed) return;
    this._retryArmed = false;
    document.removeEventListener('pointerdown', this._onGesture, true);
    document.removeEventListener('keydown', this._onGesture, true);
  }

  #syncButton() {
    this.btn?.setAttribute('aria-pressed', this.audible ? 'true' : 'false');
    this.btn?.setAttribute('aria-label', this.audible ? 'Mute video' : 'Unmute video');
  }

  // Volume fade (rAF, ease-out). iOS locks element volume at 1 — assignments
  // no-op there, so the fade degrades to an instant cut.
  #fade(v, to, ms, done) {
    cancelAnimationFrame(this._fadeRaf);
    const from = v.volume;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / ms);
      try {
        v.volume = from + (to - from) * (t * (2 - t));
      } catch (e) {
        /* volume locked (iOS) */
      }
      if (t < 1) this._fadeRaf = requestAnimationFrame(step);
      else if (done) done();
    };
    this._fadeRaf = requestAnimationFrame(step);
  }
}

if (!customElements.get('slide-sound')) {
  customElements.define('slide-sound', SlideSound);
}
